import { type FC, useState } from 'react'
import { Alert, Spinner } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { Modal } from '../../../components/Modal/Modal'
import { type InviteState, useBulkInviteMutation, useBuyAndPromoteInvitesMutation } from '../../../store/auth/auth'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useGetBillingCustomerFreeSeatsQuery } from '../../../store/plan/plan'
import { addToast } from '../../../store/toast/toastSlice'
import { readBatchBuyResult } from '../buyResult'
import { useSeatPurchaseGate } from '../seatPurchaseGate'
import { useAssistantSeatsOffered } from '../useAssistantSeatsOffered'
import { useRefreshSeats } from '../useRefreshSeats'
import { useSeatPurchasePreview } from '../useSeatPurchasePreview'
import { applyBuyOutcome, requestedPurchase } from './bulkBuyOutcome'
import { type BulkCsvRows } from './bulkCsvRows'
import { type BulkInviteResult, getBulkInviteResult } from './bulkInviteResult'
import { getBulkSeatPreview } from './bulkSeatPreview'
import { BulkInviteCsvInput } from './BulkInviteCsvInput'
import { BulkInvitePurchaseStep } from './BulkInvitePurchaseStep'
import { BulkInviteResultSummary } from './BulkInviteResultSummary'
import { BulkInviteReview } from './BulkInviteReview'
import { BulkInviteRowsTable } from './BulkInviteRowsTable'
import { BulkInviteSummary } from './BulkInviteSummary'

const NOTHING_TO_BUY = { general: 0, sidecar: 0 }
const BUY_UNCONFIRMED = 'We could not confirm the result, please try again.'

export interface BulkInvitationRevampModalProps {
  onHide: () => void
  onInvited?: (state: InviteState) => void
}

export const BulkInvitationRevampModal: FC<BulkInvitationRevampModalProps> = ({ onHide, onInvited }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const canBuy = useSeatPurchaseGate()
  const assistantOffer = useAssistantSeatsOffered()
  const refreshSeats = useRefreshSeats()
  const [bulkInvite, { isLoading: isSubmitting }] = useBulkInviteMutation()
  const [buyAndPromoteInvites, { isLoading: isBuying }] = useBuyAndPromoteInvitesMutation()
  const {
    data: freeSeatsData,
    isError: isFreeSeatsError,
    refetch: refetchFreeSeats,
  } = useGetBillingCustomerFreeSeatsQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [csv, setCsv] = useState<{ fileId: number; result: BulkCsvRows } | null>(null)
  const [pendingPurchase, setPendingPurchase] = useState<BulkInviteResult | null>(null)
  const [result, setResult] = useState<BulkInviteResult | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [buyError, setBuyError] = useState<string | null>(null)

  const purchase = pendingPurchase ? requestedPurchase(pendingPurchase) : NOTHING_TO_BUY
  const purchasePreview = useSeatPurchasePreview(purchase)

  const handleCsvParsed = (parsed: BulkCsvRows) => {
    setCsv((current) => ({ fileId: (current?.fileId ?? 0) + 1, result: parsed }))
    setResult(null)
    setSubmitError(null)
  }

  const seats = freeSeatsData?.data.seats
  const rows = csv?.result.kind === 'rows' ? csv.result.rows : null
  const preview = rows && seats && !assistantOffer.isLoading && !result && !pendingPurchase ? getBulkSeatPreview(rows, seats, assistantOffer.offered) : null
  const hasInvalidRow = preview ? preview.rows.some((previewRow) => previewRow.status === 'invalid') : false
  const isWorking = isSubmitting || isBuying

  const closeAsSent = () => {
    dispatch(addToast({ variant: 'success', title: 'Invitations sent' }))
    onInvited?.('pending')
    onHide()
  }

  const handleSubmit = async () => {
    if (!rows) {
      return
    }
    try {
      setSubmitError(null)
      const response = await bulkInvite({
        accountId,
        users: rows.map((row) => ({ email: row.email, firstName: row.firstName, lastName: row.lastName, role: row.role, roleIds: row.roleIds })),
      }).unwrap()
      refreshSeats()
      const inviteResult = getBulkInviteResult(rows, response.users)
      if (inviteResult.allSent) {
        closeAsSent()
        return
      }
      if (canBuy && inviteResult.requestedCount > 0) {
        setBuyError(null)
        setPendingPurchase(inviteResult)
        return
      }
      setResult(inviteResult)
    } catch {
      setSubmitError('Could not send the invitations, please try again.')
    }
  }

  const handleConfirmPurchase = async () => {
    if (!pendingPurchase) {
      return
    }
    try {
      setBuyError(null)
      const response = await buyAndPromoteInvites({ accountId, invitationIds: pendingPurchase.requestedIds, purchase }).unwrap()
      refreshSeats()
      if (response.checkoutRequired) {
        setBuyError(readBatchBuyResult(response, pendingPurchase.requestedIds).message ?? null)
        return
      }
      const merged = applyBuyOutcome(pendingPurchase, response.results)
      if (merged.allSent) {
        closeAsSent()
        return
      }
      setPendingPurchase(null)
      setResult(merged)
    } catch {
      setBuyError(BUY_UNCONFIRMED)
    }
  }

  const handleCancelPurchase = () => {
    if (!pendingPurchase) {
      return
    }
    setResult(pendingPurchase)
    setPendingPurchase(null)
  }

  const handleClose = () => {
    if (pendingPurchase) {
      onInvited?.('requested')
    } else if (result) {
      onInvited?.(result.requestedCount > 0 ? 'requested' : 'pending')
    }
    onHide()
  }

  return (
    <Modal
      show
      onHide={isWorking ? undefined : handleClose}
      size='xl'
      headerBodySeparator={true}
      header={<h4 className='d-flex'>{pendingPurchase ? 'Buy seats' : 'Bulk Invitation'}</h4>}
      body={
        pendingPurchase ? (
          <BulkInvitePurchaseStep purchase={purchase} invitationCount={pendingPurchase.requestedCount} preview={purchasePreview} error={buyError} />
        ) : (
          <div className='d-flex flex-column gap-3'>
            <BulkInviteCsvInput onCsvParsed={handleCsvParsed} />
            {csv?.result.kind === 'fileError' ? (
              <Alert variant='danger' className='mb-0'>
                {csv.result.message}
              </Alert>
            ) : null}
            {submitError ? (
              <Alert variant='danger' className='mb-0'>
                {submitError}
              </Alert>
            ) : null}
            {result ? (
              <div className='d-flex flex-column gap-3'>
                <BulkInviteRowsTable rows={result.rows.map((resultRow) => ({ row: resultRow.row, status: resultRow.status, errors: resultRow.errors }))} />
                <BulkInviteResultSummary result={result} canBuy={canBuy} />
              </div>
            ) : null}
            {!result && rows ? (
              preview ? (
                <div key={csv?.fileId} className='d-flex flex-column gap-3'>
                  <BulkInviteReview preview={preview} />
                  <BulkInviteSummary preview={preview} canBuy={canBuy} />
                </div>
              ) : isFreeSeatsError ? (
                <Alert variant='danger' className='d-flex align-items-center justify-content-between mb-0'>
                  Could not load the free seats.
                  <Button variant='secondary' onClick={refetchFreeSeats}>
                    Retry
                  </Button>
                </Alert>
              ) : (
                <div className='d-flex justify-content-center p-4'>
                  <Spinner size='sm' />
                </div>
              )
            ) : null}
          </div>
        )
      }
      showFooterActions={true}
      onFooterPrimaryAction={pendingPurchase ? handleConfirmPurchase : result ? handleClose : handleSubmit}
      footerPrimaryActionDisabled={pendingPurchase ? purchasePreview.isLoading || isBuying : result ? false : !preview || hasInvalidRow || isSubmitting}
      footerPrimaryActionLoading={isWorking}
      footerPrimaryActionButtonVariant='primary'
      footerPrimaryActionTitle={pendingPurchase ? 'Confirm' : result ? 'Close' : 'Continue and invite'}
      onFooterSecondaryAction={pendingPurchase ? handleCancelPurchase : undefined}
      hideSecondaryAction={result !== null}
      footerSecondaryActionDisabled={isWorking}
      backdrop={isWorking ? 'static' : undefined}
      keyboard={!isWorking}
    />
  )
}
